import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import {
  addFilledOut,
  ProgrammeActions,
  selectAllFarmerProgrammeDashboards,
  selectProgrammeActionsById,
  useAppDispatch,
  useAppSelector,
} from "model";
import { setQuestionnaireFinished } from "model/questionnaireStartedSlice";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import NavigationLock from "./NavigationLock";

export default function ProgrammesAction() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { programmeId } = useParams<{ programmeId: string }>();
  const history = useHistory();

  const allFarmerDashboards = useAppSelector(
    selectAllFarmerProgrammeDashboards
  );

  const farmerDashboards = allFarmerDashboards.filter(
    (dashboard) => dashboard.programmeId === programmeId
  );

  const farmerDashboard =
    farmerDashboards.length > 0 ? farmerDashboards[0] : null;

  const questionnaireStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).questionnaireStarted
  );

  const dispatch = useAppDispatch();

  const programmeActions = useAppSelector((state) =>
    selectProgrammeActionsById(state, programmeId)
  );
  const hasSelectedActions = !(
    !programmeActions?.selectedActions ||
    Object.keys(programmeActions.selectedActions).length === 0
  );

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMessages = (content: any) => {
      if (!content) {
        return;
      }
      // Check if submission is completed.
      if (
        typeof content.data === "object" &&
        content.data.action === "submission-completed"
      ) {
        dispatch(addFilledOut({ filledOutProgrammeId: programmeId }));
        dispatch(setQuestionnaireFinished(true));
        return;
      }
    };

    if (window.addEventListener) {
      window.addEventListener("message", handleMessages, true); // Capture
    }
    return () => {
      if (window.removeEventListener) {
        window.removeEventListener("message", handleMessages, true);
      }
    };
  }, [dispatch, programmeId]);

  // Listen to route changes and cleanup questionnaireStarted state when navigating away
  useEffect(() => {
    const unsetQuestionnaireStarted = history.listen((e) => {
      if (e.pathname !== `/programme-details/${programmeId}`) {
        // User is navigating away from programme details page
        dispatch(setQuestionnaireFinished(true));
      }
    });

    return function cleanup() {
      unsetQuestionnaireStarted();
    };
  }, [dispatch, history, programmeId]);

  // Prevent accidental page close/refresh when questionnaire is in progress
  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (questionnaireStarted.questionnaireStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireStarted.questionnaireStarted]);

  if (
    questionnaireStarted.questionnaireStarted &&
    programmeActions?.questionnaireUrl
  ) {
    return (
      <>
        <NavigationLock when={questionnaireStarted.questionnaireStarted} />
        {programmesActionsIframe(programmeActions)}
      </>
    );
  } else {
    // there simply might be no dashboard
    return <></>;
  }
}

const programmesActionsIframe = (programmesActions: ProgrammeActions) => {
  return (
    <Grid
      item
      xs={12}
      sx={{ flex: 1, display: "flex", flexDirection: "column" }}
    >
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 0,
          boxShadow: 0,
          padding: 0,
          margin: 0,
          overflow: "hidden",
          flex: 1, // Take remaining space
          display: "flex",
          flexDirection: "row",
          height: "calc(100vh)",
        }}
      >
        <iframe
          frameBorder="0"
          id="JotFormIFrame-252572906128359"
          title="New Customer Registration Form"
          allow="geolocation; microphone; camera; fullscreen; payment"
          src={programmesActions?.questionnaireUrl}
          style={{
            flex: 1,
            padding: 0,
            margin: 0,
            height: "calc(100vh)",
            width: "100%",
          }}
        ></iframe>
        <script src="https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js"></script>
        <script>
          window.jotformEmbedHandler("iframe[id='JotFormIFrame-252572906128359']",
          "https://form.jotform.com/")
        </script>
      </Box>
    </Grid>
  );
};
